import { postInfluencerOAuthConnect } from '../../redux/buzzbridge/actions';
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector, useDispatch } from 'react-redux';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { usePlanEntitlementGates } from '../../hook/usePlanEntitlementGates';
import { useDispatchPromise, showToastMessage } from '../../utils/AppUtils';
import {
    createSocialPost,
    getConnectedPlatforms,
    getInstagramAutomationStatus,
    pollSocialPostUntilSettled,
    publishSocialPost,
} from '../../redux/socialPosts/action';
import { getGlobalConfig } from '../../redux/configuration/action';
import { selectConnectedPlatforms, selectInstagramAutomationFeatureEnabled } from '../../redux/socialPosts/selector';
import { RootState } from '../../redux/root-reducer';
import type { CreateSocialPostRequest, SocialPostWithAutomation } from '../../types/instagramAutomation.types';
import { buzzBridgeConfigSelector } from '../../selectors/selectors';
import { SocialLogin } from '../SocialLogin/SocialLogin';
import { FacebookUpgradeConnect } from '../SocialLogin/FacebookUpgradeConnect';
import { needsFacebookUpgradeForInstagram, resolveInstagramConnectProvider } from '../../utils/instagramOAuthProvider';

import { InstagramGlyph, TiktokGlyph, YoutubeGlyph } from '../BuzzBridge/SocialPlatformSvgs';
import { PublishSuccessPanel } from './PublishSuccessPanel';
import { TikTokComposePanel } from '../InstagramAutomation/TikTokComposePanel';
import {
    applyTiktokToCreateBody,
    defaultTiktokFields,
    type TiktokPostFields,
} from '../InstagramAutomation/platformPostFields';
import { validateTiktokPostFields } from '../../utils/tiktokDirectPostUx';
import {
    clearSocialPublishLock,
    isSocialPublishLocked,
    setSocialPublishLock,
    type SocialPublishPlatform,
} from './socialPublishLock';

// ── Design tokens ────────────────────────────────────────────────────────────
const BG = '#0e0e0e';
const SURFACE = '#161616';
const CARD = '#1e1e1e';
const BORDER = 'rgba(255,255,255,0.07)';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const MUTED = '#7a7a7a';
const WHITE = '#f5f5f5';
const RED = '#ff6b6b';

type Platform = SocialPublishPlatform;

interface ConnectedPlatform {
    platform: Platform;
    platform_username: string;
    last_refreshed_at?: string;
}

type SocialPost = Pick<SocialPostWithAutomation, '_id' | 'status' | 'platform_post_url' | 'error_message'> & {
    published_at?: string;
};

interface PlatformState {
    platform: Platform;
    caption: string;
    title: string;
    description: string;
    tags: string; // comma-separated
    privacy_status: 'public' | 'private' | 'unlisted';
    tiktok: TiktokPostFields;
    scheduledAt: string; // ISO string or empty
    igAutomationEnabled: boolean;
    igTriggerWord: string;
    igDmMessage: string;
    igPublicReply: string;
    // publish result
    post: SocialPost | null;
    publishing: boolean;
    polling: boolean;
    publishComplete: boolean;
    error: string;
}

const DEFAULT_PLATFORM_STATE: Omit<PlatformState, 'platform'> = {
    caption: '',
    title: '',
    description: '',
    tags: '',
    privacy_status: 'public',
    tiktok: defaultTiktokFields(),
    scheduledAt: '',
    igAutomationEnabled: false,
    igTriggerWord: '',
    igDmMessage: '',
    igPublicReply: 'Thanks! Check your DMs.',
    post: null,
    publishing: false,
    polling: false,
    publishComplete: false,
    error: '',
};

const PLATFORM_LABELS: Record<Platform, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
};

// Maps platform id → enableAutoLink config key
const PLATFORM_AUTO_LINK_KEY: Record<Platform, 'Instagram' | 'TikTok' | 'YouTube'> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
};

function PlatformGlyph({ platform, size = 20 }: { platform: Platform; size?: number }) {
    const sx = { width: size, height: size };
    if (platform === 'instagram') return <InstagramGlyph sx={sx} color={WHITE} />;
    if (platform === 'tiktok') return <TiktokGlyph sx={sx} color={WHITE} />;
    return <YoutubeGlyph sx={sx} color={WHITE} />;
}

export interface PublishToSocialModalProps {
    open: boolean;
    onClose: () => void;
    submissionId: string;
    submissionLabel?: string;
    thumbnail?: string;
    /** Restrict which platforms are shown. If omitted, all platforms are shown. */
    allowedPlatforms?: Platform[];
    /** Called when a publish lock is stored so parent can disable re-publish CTA */
    onPublishActivity?: () => void;
    /** Called with the live post URL when a platform publish completes and platform_post_url is available */
    onPublishSuccess?: (platformPostUrl: string) => void;
    /** sync_upload campaigns: enforce this as the fixed scheduled publish date */
    syncPublishDate?: string;
}

function toDatetimeLocal(isoOrDate: string): string {
    const d = new Date(isoOrDate);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes(),
    )}`;
}

export const PublishToSocialModal: React.FC<PublishToSocialModalProps> = ({
    open,
    onClose,
    submissionId,
    submissionLabel,
    thumbnail,
    allowedPlatforms,
    onPublishActivity,
    onPublishSuccess,
    syncPublishDate,
}) => {
    const token = useSelector((s: RootState) => s.authentication.token);
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const connectedFromStore = useSelector(selectConnectedPlatforms);
    const instagramCommentAutomationEnabled = useSelector(selectInstagramAutomationFeatureEnabled);
    const { hasInstagramAutomation, requireInstagramAutomationEntitlement } = usePlanEntitlementGates();
    const { device } = useThemeContext();
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);

    const [loadingPlatforms, setLoadingPlatforms] = useState(false);
    const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
    const [platformStates, setPlatformStates] = useState<Record<Platform, PlatformState>>({} as any);
    const [activePlatform, setActivePlatform] = useState<Platform | null>(null);
    const [sharedCaption, setSharedCaption] = useState('');

    const fetchConnected = useCallback(() => {
        if (!token) return;
        setLoadingPlatforms(true);
        dispatchPromise({ type: getConnectedPlatforms.type, payload: { skipRefresh: true } })
            .catch(() => undefined)
            .finally(() => setLoadingPlatforms(false));
    }, [token, dispatchPromise]);

    const connected: ConnectedPlatform[] = connectedFromStore.map(p => ({
        platform: p.platform as Platform,
        platform_username: p.platform_username,
    }));

    const syncScheduledAt = syncPublishDate ? toDatetimeLocal(syncPublishDate) : '';

    useEffect(() => {
        if (open) {
            dispatch(getGlobalConfig());
            fetchConnected();
            dispatch(getInstagramAutomationStatus({ skipRefresh: true }));
            setSelectedPlatforms([]);
            setPlatformStates({} as any);
            setActivePlatform(null);
            const initialCaption = submissionLabel ?? '';
            setSharedCaption(initialCaption);
            if (submissionLabel) {
                setPlatformStates({
                    youtube: {
                        ...DEFAULT_PLATFORM_STATE,
                        platform: 'youtube',
                        title: submissionLabel,
                        description: initialCaption,
                        ...(syncScheduledAt ? { scheduledAt: syncScheduledAt } : {}),
                    },
                } as any);
            }
        }
    }, [open, fetchConnected, submissionLabel, dispatch, syncScheduledAt]);

    const connectedPlatformIds = connected.map(c => c.platform);

    const togglePlatform = (p: Platform) => {
        setSelectedPlatforms(prev => {
            if (prev.includes(p)) {
                const next = prev.filter(x => x !== p);
                setActivePlatform(next[0] ?? null);
                return next;
            }
            const next = [...prev, p];
            if (!activePlatform) setActivePlatform(p);
            if (!platformStates[p]) {
                setPlatformStates(s => ({
                    ...s,
                    [p]: {
                        ...DEFAULT_PLATFORM_STATE,
                        platform: p,
                        ...(p === 'youtube'
                            ? {
                                  title: submissionLabel ?? '',
                                  description: sharedCaption,
                              }
                            : {
                                  caption: sharedCaption,
                                  ...(p === 'tiktok' ? { tiktok: defaultTiktokFields() } : {}),
                              }),
                        ...(p === 'instagram'
                            ? {
                                  igAutomationEnabled: instagramCommentAutomationEnabled && hasInstagramAutomation,
                              }
                            : {}),
                        ...(syncScheduledAt ? { scheduledAt: syncScheduledAt } : {}),
                    },
                }));
            }
            return next;
        });
    };

    const updateState = (p: Platform, patch: Partial<PlatformState>) => {
        setPlatformStates(s => ({ ...s, [p]: { ...s[p], ...patch } }));
    };

    const beginAsyncPublishFlow = async (p: Platform, post: SocialPost) => {
        setSocialPublishLock(submissionId, p, post._id);
        onPublishActivity?.();

        updateState(p, {
            post,
            publishing: false,
            polling: true,
            publishComplete: false,
            error: '',
        });

        try {
            const finalPost = (await dispatchPromise({
                type: pollSocialPostUntilSettled.type,
                payload: { postId: post._id },
            })) as SocialPostWithAutomation;
            if (finalPost.status === 'failed') {
                clearSocialPublishLock(submissionId, p);
                onPublishActivity?.();
                updateState(p, {
                    post: finalPost,
                    polling: false,
                    publishComplete: false,
                    error: finalPost.error_message ?? 'Publishing failed on the platform.',
                });
                return;
            }
            updateState(p, {
                post: finalPost,
                polling: false,
                publishComplete: true,
                error: '',
            });
            if (finalPost.platform_post_url) {
                onPublishSuccess?.(finalPost.platform_post_url);
            }
        } catch (err: unknown) {
            const msg =
                (err as { data?: { message?: string }; message?: string })?.data?.message ??
                (err instanceof Error ? err.message : 'Publishing failed. Please try again.');
            clearSocialPublishLock(submissionId, p);
            onPublishActivity?.();
            updateState(p, { polling: false, publishComplete: false, error: msg });
        }
    };

    const publishPlatform = async (p: Platform) => {
        const ps = platformStates[p];
        if (isSocialPublishLocked(submissionId, p)) return;

        updateState(p, { publishing: true, polling: false, publishComplete: false, error: '' });

        try {
            const body: CreateSocialPostRequest = {
                platform: p,
                submission_id: submissionId,
                ...(p === 'youtube'
                    ? {
                          title: ps.title,
                          description: ps.description,
                          tags: ps.tags
                              .split(',')
                              .map(t => t.trim())
                              .filter(Boolean),
                          privacy_status: ps.privacy_status,
                      }
                    : { caption: ps.caption || sharedCaption }),
                ...(p === 'tiktok' ? applyTiktokToCreateBody({ platform: p }, ps.tiktok ?? defaultTiktokFields()) : {}),
                ...(p === 'instagram' && ps.igAutomationEnabled && instagramCommentAutomationEnabled
                    ? {
                          instagram_automation: {
                              enabled: true,
                              trigger_word: ps.igTriggerWord.trim(),
                              dm_message: ps.igDmMessage.trim(),
                              public_reply_text: ps.igPublicReply.trim() || 'Thanks! Check your DMs.',
                          },
                      }
                    : {}),
                ...(ps.scheduledAt ? { scheduled_at: ps.scheduledAt } : {}),
            };

            let post = (await dispatchPromise({
                type: createSocialPost.type,
                payload: { body },
            })) as SocialPost;
            updateState(p, { post });

            if (ps.scheduledAt) {
                setSocialPublishLock(submissionId, p, post._id);
                onPublishActivity?.();
                updateState(p, { publishing: false, publishComplete: true });
                return;
            }

            post = (await dispatchPromise({
                type: publishSocialPost.type,
                payload: { postId: post._id },
            })) as SocialPost;

            if (post.status === 'published') {
                setSocialPublishLock(submissionId, p, post._id);
                onPublishActivity?.();
                updateState(p, { post, publishing: false, polling: false, publishComplete: true, error: '' });
                if (post.platform_post_url) {
                    onPublishSuccess?.(post.platform_post_url);
                }
                return;
            }

            if (post.status === 'publishing') {
                await beginAsyncPublishFlow(p, post);
                return;
            }

            updateState(p, { post, publishing: false, publishComplete: true });
            if (post.platform_post_url) {
                onPublishSuccess?.(post.platform_post_url);
            }
        } catch (err: unknown) {
            const msg =
                (err as { data?: { message?: string } })?.data?.message ?? 'Publishing failed. Please try again.';
            updateState(p, { publishing: false, polling: false, error: msg });
        }
    };

    const retryPublish = async (p: Platform) => {
        const ps = platformStates[p];
        if (!ps.post?._id || ps.post.status !== 'failed') return;
        updateState(p, { publishing: true, polling: false, publishComplete: false, error: '' });
        try {
            const post = (await dispatchPromise({
                type: publishSocialPost.type,
                payload: { postId: ps.post._id },
            })) as SocialPost;
            if (post.status === 'published') {
                updateState(p, { post, publishing: false, polling: false, publishComplete: true, error: '' });
                return;
            }
            if (post.status === 'publishing') {
                await beginAsyncPublishFlow(p, post);
                return;
            }
            updateState(p, { post, publishing: false, publishComplete: true });
        } catch (err: unknown) {
            const msg = (err as { data?: { message?: string } })?.data?.message ?? 'Retry failed.';
            updateState(p, { publishing: false, error: msg });
        }
    };

    const isPlatformComplete = (p: Platform) => {
        const ps = platformStates[p];
        if (!ps) return false;
        if (ps.scheduledAt && ps.post?.status === 'scheduled') return true;
        return Boolean(ps.publishComplete);
    };

    const allDone = selectedPlatforms.length > 0 && selectedPlatforms.every(p => isPlatformComplete(p));

    const canPublish = (p: Platform) => {
        const ps = platformStates[p];
        if (!ps) return false;
        if (p === 'youtube') return Boolean(ps.title.trim());
        if (p === 'instagram' && ps.igAutomationEnabled && instagramCommentAutomationEnabled) {
            return Boolean(ps.igTriggerWord.trim() && ps.igDmMessage.trim());
        }
        if (p === 'tiktok') {
            return validateTiktokPostFields(ps.tiktok ?? defaultTiktokFields()).valid;
        }
        return true; // caption optional for IG
    };

    const tiktokConnectedRow = connectedFromStore.find(c => c.platform === 'tiktok');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={device.isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BG,
                    backgroundImage: 'none',
                    borderRadius: device.isMobile ? 0 : 4,
                    border: device.isMobile ? 'none' : `1px solid ${BORDER}`,
                    overflow: 'hidden',
                    m: device.isMobile ? 0 : 2,
                    ...(device.isMobile ? { height: '100dvh', maxHeight: '100dvh' } : {}),
                },
            }}>
            {/* Header */}
            <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: WHITE }}>
                    Publish to Social
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: MUTED, '&:hover': { color: WHITE } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box
                sx={{
                    px: 3,
                    pb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    maxHeight: '75vh',
                    overflowY: 'auto',
                }}>
                {/* Submission context */}
                {(submissionLabel || thumbnail) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            bgcolor: SURFACE,
                            borderRadius: 2,
                            px: 2,
                            py: 1.25,
                            border: `1px solid ${BORDER}`,
                        }}>
                        {thumbnail && (
                            <Box
                                component="img"
                                src={thumbnail}
                                alt={submissionLabel}
                                sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                            />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                Publishing
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: WHITE }} noWrap>
                                {submissionLabel ?? 'Approved submission'}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                px: 1.25,
                                py: 0.4,
                                borderRadius: 999,
                                bgcolor: 'rgba(0,253,193,0.1)',
                                flexShrink: 0,
                            }}>
                            <Typography sx={{ fontSize: 10, fontFamily: FONT_ENUM.MAIN_BOLD, color: MINT }}>
                                Approved
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Step 1: Platform selection */}
                <Box>
                    <Typography
                        sx={{
                            fontSize: 10,
                            color: MUTED,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            mb: 1.5,
                        }}>
                        Choose platforms
                    </Typography>
                    <TextField
                        size="small"
                        label="Caption (Instagram & TikTok)"
                        value={sharedCaption}
                        onChange={e => {
                            const value = e.target.value;
                            setSharedCaption(value);
                            setPlatformStates(s => {
                                const next = { ...s };
                                (['instagram', 'tiktok'] as Platform[]).forEach(p => {
                                    if (next[p]) next[p] = { ...next[p], caption: value };
                                });
                                if (next.youtube) {
                                    next.youtube = { ...next.youtube, description: value };
                                }
                                return next;
                            });
                        }}
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ ...textFieldSx, mb: 1 }}
                    />
                    <Typography sx={{ fontSize: 11, color: MUTED, mb: 2, lineHeight: 1.4 }}>
                        Caption for Instagram & TikTok. YouTube uses its own title and description on the YouTube card.
                        TikTok privacy, interactions, and commercial settings are on the TikTok card only.
                    </Typography>
                    {loadingPlatforms ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} sx={{ color: GOLD }} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {(['instagram', 'tiktok', 'youtube'] as Platform[])
                                .filter(p => !allowedPlatforms || allowedPlatforms.includes(p))
                                .map(p => {
                                    const isConnected = connectedPlatformIds.includes(p);
                                    const isSelected = selectedPlatforms.includes(p);
                                    const connInfo = connected.find(c => c.platform === p);
                                    const ps = platformStates[p];
                                    const platformLocked = isSocialPublishLocked(submissionId, p);
                                    const publishComplete = Boolean(ps?.publishComplete);
                                    const published = publishComplete;
                                    const failed =
                                        (ps?.post?.status === 'failed' && !publishComplete) || Boolean(ps?.error);
                                    const scheduled = ps?.scheduledAt && ps?.post?.status === 'scheduled';
                                    const isPolling = Boolean(ps?.polling);
                                    const autoLinkEnabled =
                                        buzzBridgeConfig?.enableAutoLink?.[PLATFORM_AUTO_LINK_KEY[p]] !== false;

                                    return (
                                        <Box
                                            key={p}
                                            sx={{
                                                borderRadius: 2,
                                                border: `1px solid ${
                                                    published
                                                        ? 'rgba(0,253,193,0.3)'
                                                        : failed
                                                        ? 'rgba(255,107,107,0.3)'
                                                        : isSelected
                                                        ? `rgba(255,201,101,0.3)`
                                                        : BORDER
                                                }`,
                                                bgcolor: isSelected ? 'rgba(255,201,101,0.04)' : CARD,
                                                overflow: 'hidden',
                                            }}>
                                            {/* Platform row */}
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    cursor: isConnected && autoLinkEnabled ? 'pointer' : 'default',
                                                }}
                                                onClick={() =>
                                                    isConnected &&
                                                    autoLinkEnabled &&
                                                    !published &&
                                                    !platformLocked &&
                                                    togglePlatform(p)
                                                }>
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 2,
                                                        bgcolor: SURFACE,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}>
                                                    <PlatformGlyph platform={p} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: 14,
                                                            color: WHITE,
                                                        }}>
                                                        {PLATFORM_LABELS[p]}
                                                    </Typography>
                                                    {isConnected && autoLinkEnabled ? (
                                                        <Typography sx={{ fontSize: 11, color: MINT }}>
                                                            @{connInfo!.platform_username}
                                                        </Typography>
                                                    ) : (
                                                        <Typography sx={{ fontSize: 11, color: MUTED }}>
                                                            {autoLinkEnabled
                                                                ? 'Not connected'
                                                                : 'Manual publish required'}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {published && <CheckCircleIcon sx={{ color: MINT, fontSize: 20 }} />}
                                                {scheduled && (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 11,
                                                            color: GOLD,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        }}>
                                                        Scheduled
                                                    </Typography>
                                                )}
                                                {isConnected &&
                                                    autoLinkEnabled &&
                                                    needsFacebookUpgradeForInstagram(connInfo as any) && (
                                                        <Box sx={{ width: '100%', mt: 1 }}>
                                                            <FacebookUpgradeConnect
                                                                compact
                                                                onCodeReceived={code => {
                                                                    void dispatchPromise(
                                                                        postInfluencerOAuthConnect({
                                                                            code,
                                                                            provider: 'meta',
                                                                        }),
                                                                    )
                                                                        .then(() => fetchConnected())
                                                                        .catch((err: any) => {
                                                                            const msg =
                                                                                err?.data?.message ||
                                                                                err?.response?.data?.message ||
                                                                                err?.message ||
                                                                                'Could not upgrade Facebook connection.';
                                                                            showToastMessage(msg, 'error');
                                                                        });
                                                                }}
                                                                onAuthComplete={ok => {
                                                                    if (!ok) fetchConnected();
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                {!isConnected && autoLinkEnabled && (
                                                    <SocialLogin
                                                        provider={
                                                            p === 'instagram'
                                                                ? resolveInstagramConnectProvider(buzzBridgeConfig)
                                                                : p
                                                        }
                                                        onCodeReceived={code => {
                                                            void dispatchPromise(
                                                                postInfluencerOAuthConnect({
                                                                    code,
                                                                    provider:
                                                                        p === 'instagram'
                                                                            ? resolveInstagramConnectProvider(
                                                                                  buzzBridgeConfig,
                                                                              )
                                                                            : p,
                                                                }),
                                                            )
                                                                .then(() => fetchConnected())
                                                                .catch((err: any) => {
                                                                    const msg =
                                                                        err?.data?.message ||
                                                                        err?.response?.data?.message ||
                                                                        err?.message ||
                                                                        'Could not connect this account. Try again.';
                                                                    showToastMessage(msg, 'error');
                                                                });
                                                        }}
                                                        onAuthComplete={ok => {
                                                            if (!ok) fetchConnected();
                                                        }}
                                                        customButton={
                                                            <Button
                                                                size="small"
                                                                sx={{
                                                                    fontSize: 11,
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: GOLD,
                                                                    border: `1px solid rgba(255,201,101,0.3)`,
                                                                    borderRadius: 999,
                                                                    px: 1.5,
                                                                    py: 0.5,
                                                                    textTransform: 'none',
                                                                    '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
                                                                }}>
                                                                Connect
                                                            </Button>
                                                        }
                                                    />
                                                )}
                                            </Box>
                                            {!autoLinkEnabled && (
                                                <Box
                                                    sx={{
                                                        px: 2,
                                                        pb: 1.5,
                                                        borderTop: `1px solid ${BORDER}`,
                                                        pt: 1.5,
                                                    }}>
                                                    <Typography sx={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
                                                        Auto-publish is not available for{' '}
                                                        <Box
                                                            component="span"
                                                            sx={{ color: WHITE, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                                            {PLATFORM_LABELS[p]}
                                                        </Box>
                                                        . Please publish your content directly on{' '}
                                                        <Box
                                                            component="span"
                                                            sx={{ color: WHITE, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                                            {PLATFORM_LABELS[p]}
                                                        </Box>
                                                        , then come back and paste the live URL in the{' '}
                                                        <Box
                                                            component="span"
                                                            sx={{ color: GOLD, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                                            Submit live URL
                                                        </Box>{' '}
                                                        field below.
                                                    </Typography>
                                                </Box>
                                            )}

                                            {platformLocked && !published && (
                                                <Box sx={{ px: 2, pb: 1.5, borderTop: `1px solid ${BORDER}` }}>
                                                    <Typography sx={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
                                                        Publish already in progress for this submission. Please wait a
                                                        few minutes before trying again.
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Compose fields (shown when selected and not yet published) */}
                                            {isSelected && !published && ps && !platformLocked && (
                                                <Box
                                                    sx={{
                                                        px: 2,
                                                        pb: 2,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1.5,
                                                        borderTop: `1px solid ${BORDER}`,
                                                        pt: 2,
                                                    }}
                                                    onClick={e => e.stopPropagation()}>
                                                    {/* Caption / Title */}
                                                    {p === 'youtube' ? (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                label="Title *"
                                                                value={ps.title}
                                                                onChange={e =>
                                                                    updateState(p, { title: e.target.value })
                                                                }
                                                                fullWidth
                                                                sx={textFieldSx}
                                                            />
                                                            <TextField
                                                                size="small"
                                                                label="Description"
                                                                value={ps.description}
                                                                onChange={e =>
                                                                    updateState(p, { description: e.target.value })
                                                                }
                                                                fullWidth
                                                                multiline
                                                                minRows={2}
                                                                sx={textFieldSx}
                                                            />
                                                            <TextField
                                                                size="small"
                                                                label="Tags (comma-separated)"
                                                                value={ps.tags}
                                                                onChange={e => updateState(p, { tags: e.target.value })}
                                                                fullWidth
                                                                sx={textFieldSx}
                                                            />
                                                            <Box>
                                                                <Typography
                                                                    sx={{ fontSize: 10, color: MUTED, mb: 0.5 }}>
                                                                    Privacy
                                                                </Typography>
                                                                <Select
                                                                    size="small"
                                                                    value={ps.privacy_status}
                                                                    onChange={e =>
                                                                        updateState(p, {
                                                                            privacy_status: e.target.value as any,
                                                                        })
                                                                    }
                                                                    fullWidth
                                                                    sx={selectSx}>
                                                                    <MenuItem value="public">Public</MenuItem>
                                                                    <MenuItem value="unlisted">Unlisted</MenuItem>
                                                                    <MenuItem value="private">Private</MenuItem>
                                                                </Select>
                                                            </Box>
                                                        </>
                                                    ) : p === 'instagram' ? (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                label="Caption (optional)"
                                                                value={ps.caption}
                                                                onChange={e =>
                                                                    updateState(p, { caption: e.target.value })
                                                                }
                                                                fullWidth
                                                                multiline
                                                                minRows={2}
                                                                sx={textFieldSx}
                                                            />
                                                            {instagramCommentAutomationEnabled && (
                                                                <Box
                                                                    sx={{
                                                                        p: 1.5,
                                                                        borderRadius: 1.5,
                                                                        border: `1px solid ${BORDER}`,
                                                                        bgcolor: SURFACE,
                                                                    }}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                size="small"
                                                                                checked={ps.igAutomationEnabled}
                                                                                onChange={e => {
                                                                                    const checked = e.target.checked;
                                                                                    if (
                                                                                        checked &&
                                                                                        !requireInstagramAutomationEntitlement()
                                                                                    ) {
                                                                                        return;
                                                                                    }
                                                                                    updateState(p, {
                                                                                        igAutomationEnabled: checked,
                                                                                    });
                                                                                }}
                                                                                sx={{
                                                                                    color: MUTED,
                                                                                    '&.Mui-checked': { color: GOLD },
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Typography
                                                                                sx={{ fontSize: 12, color: WHITE }}>
                                                                                Comment-to-DM automation
                                                                                {!hasInstagramAutomation
                                                                                    ? ' (Pro)'
                                                                                    : ''}
                                                                            </Typography>
                                                                        }
                                                                    />
                                                                    {ps.igAutomationEnabled && (
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                gap: 1,
                                                                                mt: 1,
                                                                            }}>
                                                                            <TextField
                                                                                size="small"
                                                                                label="Trigger comment *"
                                                                                value={ps.igTriggerWord}
                                                                                onChange={e =>
                                                                                    updateState(p, {
                                                                                        igTriggerWord: e.target.value,
                                                                                    })
                                                                                }
                                                                                sx={textFieldSx}
                                                                            />
                                                                            <TextField
                                                                                size="small"
                                                                                label="DM message *"
                                                                                value={ps.igDmMessage}
                                                                                onChange={e =>
                                                                                    updateState(p, {
                                                                                        igDmMessage: e.target.value,
                                                                                    })
                                                                                }
                                                                                multiline
                                                                                minRows={2}
                                                                                sx={textFieldSx}
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </>
                                                    ) : null}

                                                    {p === 'tiktok' && (
                                                        <TikTokComposePanel
                                                            fields={ps.tiktok ?? defaultTiktokFields()}
                                                            onChange={patch =>
                                                                updateState(p, {
                                                                    tiktok: {
                                                                        ...(ps.tiktok ?? defaultTiktokFields()),
                                                                        ...patch,
                                                                    },
                                                                })
                                                            }
                                                            active={isSelected && isConnected && autoLinkEnabled}
                                                            mediaPreviewUrl={thumbnail}
                                                            creatorNicknameFallback={
                                                                tiktokConnectedRow?.creator_nickname
                                                            }
                                                            theme={{
                                                                muted: MUTED,
                                                                white: WHITE,
                                                                gold: GOLD,
                                                                red: RED,
                                                                mint: MINT,
                                                                surface: SURFACE,
                                                                border: BORDER,
                                                            }}
                                                        />
                                                    )}

                                                    {/* Schedule picker */}
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10,
                                                                color: syncPublishDate ? GOLD : MUTED,
                                                                mb: 0.5,
                                                            }}>
                                                            {syncPublishDate
                                                                ? 'Sync publish date (fixed by campaign — cannot be changed)'
                                                                : 'Schedule (optional — leave empty to publish now)'}
                                                        </Typography>
                                                        <Box
                                                            component="input"
                                                            type="datetime-local"
                                                            value={ps.scheduledAt}
                                                            readOnly={Boolean(syncPublishDate)}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                if (syncPublishDate) return;
                                                                updateState(p, { scheduledAt: e.target.value });
                                                            }}
                                                            sx={{
                                                                width: '100%',
                                                                px: 1.5,
                                                                py: 1,
                                                                borderRadius: 1.5,
                                                                border: `1px solid ${
                                                                    syncPublishDate ? `${GOLD}40` : BORDER
                                                                }`,
                                                                bgcolor: SURFACE,
                                                                color: syncPublishDate ? GOLD : WHITE,
                                                                fontSize: 13,
                                                                outline: 'none',
                                                                boxSizing: 'border-box',
                                                                colorScheme: 'dark',
                                                                cursor: syncPublishDate ? 'not-allowed' : 'auto',
                                                                opacity: syncPublishDate ? 0.85 : 1,
                                                            }}
                                                        />
                                                    </Box>

                                                    {/* Error */}
                                                    {ps.error && (
                                                        <Typography sx={{ fontSize: 12, color: RED, lineHeight: 1.5 }}>
                                                            {ps.error}
                                                        </Typography>
                                                    )}

                                                    {/* Publish button */}
                                                    <Button
                                                        fullWidth
                                                        disabled={
                                                            ps.publishing ||
                                                            isPolling ||
                                                            platformLocked ||
                                                            !canPublish(p)
                                                        }
                                                        onClick={() =>
                                                            ps.error && ps.post ? retryPublish(p) : publishPlatform(p)
                                                        }
                                                        sx={{
                                                            py: 1.25,
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: 13,
                                                            background: canPublish(p)
                                                                ? `linear-gradient(90deg, ${GOLD}, #feb700)`
                                                                : 'rgba(255,255,255,0.06)',
                                                            color: canPublish(p) ? '#392700' : MUTED,
                                                            '&.Mui-disabled': {
                                                                background: 'rgba(255,255,255,0.06)',
                                                                color: MUTED,
                                                            },
                                                        }}>
                                                        {ps.publishing ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                                                Starting publish…
                                                            </Box>
                                                        ) : isPolling ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                                                Waiting for {PLATFORM_LABELS[p]}…
                                                            </Box>
                                                        ) : ps.error && ps.post ? (
                                                            'Retry'
                                                        ) : ps.scheduledAt ? (
                                                            'Schedule post'
                                                        ) : (
                                                            `Publish to ${PLATFORM_LABELS[p]}`
                                                        )}
                                                    </Button>
                                                </Box>
                                            )}

                                            {isPolling && ps && !publishComplete && (
                                                <Box
                                                    sx={{
                                                        px: 2,
                                                        pb: 2,
                                                        borderTop: `1px solid ${BORDER}`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}>
                                                    <CircularProgress size={18} sx={{ color: GOLD }} />
                                                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                                                        Publishing in progress — checking status (up to 2 minutes)…
                                                    </Typography>
                                                </Box>
                                            )}

                                            {published && ps && (
                                                <PublishSuccessPanel
                                                    platformLabel={PLATFORM_LABELS[p]}
                                                    platformPostUrl={ps.post?.platform_post_url}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                        </Box>
                    )}
                </Box>

                {/* Done button */}
                {allDone && (
                    <Button
                        fullWidth
                        onClick={onClose}
                        sx={{
                            py: 1.5,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            bgcolor: MINT,
                            color: '#0a0a0a',
                            '&:hover': { bgcolor: '#00e0ab' },
                        }}>
                        Done
                    </Button>
                )}
            </Box>
        </Dialog>
    );
};

// ── Shared field styles ───────────────────────────────────────────────────────
const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: '#161616',
        color: '#f5f5f5',
        borderRadius: 1.5,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.07)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,201,101,0.5)' },
    },
    '& .MuiInputLabel-root': { color: '#7a7a7a' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#ffc965' },
};

const selectSx = {
    bgcolor: '#161616',
    color: '#f5f5f5',
    borderRadius: 1.5,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.07)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,201,101,0.5)' },
    '& .MuiSvgIcon-root': { color: '#7a7a7a' },
};
